'use client';

import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  Fade
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const STORAGE_KEY = 'ad_last_shown';
const ONE_DAY_MS = 24 * 60 * 60 * 1000; // 24小时

interface AdPopupProps {
  imageUrl?: string;
  delay?: number; // 延迟显示时间，单位毫秒
  onOpenPayment?: () => void; // 打开支付对话框的回调
}

export default function AdPopup({
  imageUrl = '/imgs/guanggao.jpg',
  delay = 2000,
  onOpenPayment
}: AdPopupProps) {
  const [show, setShow] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const shouldShow = () => {
      // 只有第三方公司才显示广告
      if (!user || user.organization_type !== 'third_party') {
        return false;
      }

      const lastShown = localStorage.getItem(STORAGE_KEY);
      if (!lastShown) return true;

      const now = Date.now();
      return (now - Number(lastShown)) > ONE_DAY_MS;
    };

    if (shouldShow()) {
      const timer = setTimeout(() => {
        setShow(true);
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [location.pathname, delay, user]); // 监听路由变化和用户信息

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setShow(false);
  };

  const handleClick = () => {
    if (onOpenPayment) {
      onOpenPayment();
    }
    handleClose();
  };

  if (!show) return null;

  return (
    <Fade in={show} timeout={500}>
      <Box
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 9999,
          cursor: 'pointer',
        }}
      >
        <Card
          sx={{
            position: 'relative',
            width: 300,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            overflow: 'visible',
          }}
          onClick={handleClick}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            sx={{
              position: 'absolute',
              top: -10,
              right: -10,
              bgcolor: 'grey.100',
              '&:hover': {
                bgcolor: 'grey.200',
              },
              zIndex: 10,
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <CardContent sx={{ p: 0, '&:last-child': { p: 0 } }}>
            <img
              src={imageUrl}
              alt="广告"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                borderRadius: '8px',
              }}
            />
          </CardContent>
        </Card>
      </Box>
    </Fade>
  );
}
